import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildActionLogQuery,
  mapActionLogSearchResponseToDto,
} from '../dto/action-log-explorer.dto.js';

const DEFAULT_FILTERS = Object.freeze({
  actorUid: '',
  profileUid: '',
  action: '',
  from: '',
  to: '',
  ip: '',
  supportGrantId: '',
  requestId: '',
  order: 'desc',
  limit: '50',
});

export function useActionLogExplorer() {
  const { token } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const filtersRef = useRef(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const pageRef = useRef(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success] = useState('');

  const loadRows = useCallback(async (nextFilters = filtersRef.current, nextPage = pageRef.current) => {
    setIsLoading(true);
    setError('');

    const limit = Number.parseInt(nextFilters.limit, 10) || 50;
    const query = buildActionLogQuery({
      ...nextFilters,
      offset: nextPage * limit,
    });

    try {
      const response = await adminApi.searchActionLogs(token, query);
      const dto = mapActionLogSearchResponseToDto(response);
      setRows(dto.rows);
      setTotal(dto.total);
    } catch (requestError) {
      setRows([]);
      setTotal(0);
      setError(requestError?.message || 'Failed to load action log records.');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRows(DEFAULT_FILTERS, 0);
  }, [loadRows]);

  const updateFilter = useCallback((name, value) => {
    filtersRef.current = {
      ...filtersRef.current,
      [name]: value,
    };
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const applyFilters = useCallback(async () => {
    pageRef.current = 0;
    setPage(0);
    setFilters(filtersRef.current);
    await loadRows(filtersRef.current, 0);
  }, [loadRows]);

  const resetFilters = useCallback(async () => {
    filtersRef.current = DEFAULT_FILTERS;
    pageRef.current = 0;
    setFilters(DEFAULT_FILTERS);
    setPage(0);
    await loadRows(DEFAULT_FILTERS, 0);
  }, [loadRows]);

  const refresh = useCallback(() => loadRows(filtersRef.current, pageRef.current), [loadRows]);

  const nextPage = useCallback(async () => {
    const next = pageRef.current + 1;
    pageRef.current = next;
    setPage(next);
    await loadRows(filtersRef.current, next);
  }, [loadRows]);

  const previousPage = useCallback(async () => {
    const next = Math.max(0, pageRef.current - 1);
    pageRef.current = next;
    setPage(next);
    await loadRows(filtersRef.current, next);
  }, [loadRows]);

  const limit = Number.parseInt(filters.limit, 10) || 50;

  return {
    rows,
    total,
    filters,
    page,
    isLoading,
    error,
    success,
    canGoPrevious: page > 0,
    canGoNext: rows.length >= limit,
    updateFilter,
    applyFilters,
    resetFilters,
    refresh,
    nextPage,
    previousPage,
  };
}
